import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useQueueSettings from '@/hooks/dq/useSettings';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

const Dialog = styled(StyledDialog)`
  width: 464px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 56px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

export type FormDataT = {
  page_id: number;
  frequency: number;
  slack_webhook_url: string;
  text_status_slack_channel_id: string;
  comments_checker_disabled: boolean;
};

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const FrequencyModal: FC<Props> = ({ activePage, onClose }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const [network, networkLabel] = useNetwork();

  const settings = useQueueSettings(activePage);

  const {
    control,
    formState: { isSubmitting },
    setError,
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      page_id: activePage.id,
      frequency: (settings?.frequency ?? 30) * 60,
      slack_webhook_url: settings?.slackWebhookUrl ?? '',
      text_status_slack_channel_id: settings?.textStatusSlackChannelId ?? '',
      comments_checker_disabled: settings?.commentsCheckerDisabled ?? true,
    },
  });

  const { data: pages } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
    },
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.post(`/v1/editorial/queue/settings`, {
        ...data,
        frequency: Math.floor(data.frequency / 60),
        network,
      });

      await queryClient.invalidateQueries([
        'editorial_distribution_queue_channels',
      ]);

      onClose();

      toasts.create('success', 'Saved');
    } catch (err) {
      handleFormErrors(err, setError, alerts);
    }
  });

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Frequency</Heading>
        <FormLayout>
          <FormField<FormDataT, number>
            type="select"
            control={control}
            name="page_id"
            label="Page Name"
            placeholder="Select"
            disabled
            options={pages?.map((p) => ({ value: p.id, label: p.name })) ?? []}
          />
          <Larger>
            <Label>Network</Label>
            <NetworkInput disabled value={networkLabel} />
          </Larger>
          <FormField<FormDataT>
            type="duration"
            control={control}
            name="frequency"
            label="Frequency"
            minutesOnly
          />
          <Larger>
            <Label>Reach Duration (min)</Label>
            <Input disabled value="60" />
          </Larger>
          <FormField<FormDataT>
            type="text"
            inputType="url"
            control={control}
            name="slack_webhook_url"
            label="Slack Webhook Url"
            placeholder="Add url"
          />
          <FormField<FormDataT>
            type="text"
            control={control}
            name="text_status_slack_channel_id"
            label="Slack Channel ID"
            placeholder="Add channel ID"
          />
          {network === EditorialFacebookPostNetwork.FACEBOOK && (
            <FormField<FormDataT>
              headline="Comments checker disabled"
              control={control}
              name="comments_checker_disabled"
              type="switch"
            />
          )}
        </FormLayout>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton
            type="button"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Save
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default FrequencyModal;
